import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import AppRoutes from './routes/AppRoutes';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create enhanced theme with custom color palette
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#7D1F8E', // Primary purple
      light: '#A855C7', // Lighter purple
      dark: '#5B1A6B', // Darker purple
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#EF7504', // Primary orange
      light: '#FF9A3C', // Lighter orange
      dark: '#CC5F00', // Darker orange
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D2565A', // Secondary red
      light: '#E07B7F', // Lighter red
      dark: '#B83C78', // Secondary pink/purple
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
    },
    info: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C2C2C',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(125, 31, 142, 0.1)',
    '0px 4px 8px rgba(125, 31, 142, 0.12)',
    '0px 6px 12px rgba(125, 31, 142, 0.14)',
    '0px 8px 16px rgba(125, 31, 142, 0.16)',
    '0px 10px 20px rgba(125, 31, 142, 0.18)',
    '0px 12px 24px rgba(125, 31, 142, 0.2)',
    '0px 14px 28px rgba(125, 31, 142, 0.22)',
    '0px 16px 32px rgba(125, 31, 142, 0.24)',
    '0px 18px 36px rgba(125, 31, 142, 0.26)',
    '0px 20px 40px rgba(125, 31, 142, 0.28)',
    '0px 22px 44px rgba(125, 31, 142, 0.3)',
    '0px 24px 48px rgba(125, 31, 142, 0.32)',
    '0px 26px 52px rgba(125, 31, 142, 0.34)',
    '0px 28px 56px rgba(125, 31, 142, 0.36)',
    '0px 30px 60px rgba(125, 31, 142, 0.38)',
    '0px 32px 64px rgba(125, 31, 142, 0.4)',
    '0px 34px 68px rgba(125, 31, 142, 0.42)',
    '0px 36px 72px rgba(125, 31, 142, 0.44)',
    '0px 38px 76px rgba(125, 31, 142, 0.46)',
    '0px 40px 80px rgba(125, 31, 142, 0.48)',
    '0px 42px 84px rgba(125, 31, 142, 0.5)',
    '0px 44px 88px rgba(125, 31, 142, 0.52)',
    '0px 46px 92px rgba(125, 31, 142, 0.54)',
    '0px 48px 96px rgba(125, 31, 142, 0.56)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0px 4px 12px rgba(125, 31, 142, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 24px rgba(125, 31, 142, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #7D1F8E 0%, #B83C78 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5B1A6B 0%, #9A2F5F 100%)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 8px 32px rgba(125, 31, 142, 0.12)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 16px 48px rgba(125, 31, 142, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#7D1F8E',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#7D1F8E',
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
